import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useClaimAllMerchantActivations } from '@givebacks/api/offers';
import { ControlPosition, MapControl } from '@vis.gl/react-google-maps';
import { useSelectHasEitherCardTypes } from 'core/Api';
import { Button, Container, notifications } from 'ui';

import { useInStoreLocations } from '../EarnInStore/useInStoreLocations';

const ActivateAllOffersButton = () => {
  const { locations } = useInStoreLocations();
  const { mutate: claimAll, isPending: isLoading } = useClaimAllMerchantActivations();
  const { data: hasPartnerCard } = useSelectHasEitherCardTypes();
  const { isAuthenticated } = useAuthentication();

  const isUnclaimedOffers = useMemo(() => {
    if (!locations.length) return false;
    return locations.some(
      (location) => !(location.merchant.dining || location.merchant.activation?.active)
    );
  }, [locations]);

  const merchantIds = useMemo(() => {
    if (!locations.length) return [];
    return locations
      .filter((location) => !(location.merchant.dining || location.merchant.activation?.active))
      .map(({ merchant }) => merchant.uuid);
  }, [locations]);

  const handleClaimAll = useCallback(() => {
    claimAll(merchantIds, {
      onSuccess: () => {
        notifications.show({ message: 'All offers activated', variant: 'success' });
      },
      onError: (e) => {
        if (typeof e === 'string') {
          notifications.show({ message: e, variant: 'error' });
        }
      },
    });
  }, [claimAll, merchantIds]);

  if (!isUnclaimedOffers) return null;
  if (!isAuthenticated || !hasPartnerCard) return null;

  return (
    <Button onClick={handleClaimAll} loading={isLoading}>
      Activate All Offers
    </Button>
  );
};

type ActivateAllOffersMapControlProps = {
  controlPosition: ControlPosition;
};
const ActivateAllOffersMapControl = ({ controlPosition }: ActivateAllOffersMapControlProps) => {
  return (
    <MapControl position={controlPosition}>
      <Container p="md">
        <ActivateAllOffersButton />
      </Container>
    </MapControl>
  );
};

export { ActivateAllOffersMapControl };
